import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { IconBuilding, IconRefresh, IconSearch } from '../components/admin/icons';

interface ClientCard {
  linkId: number;
  orgId: number;
  orgName: string;
  orgSlug: string;
  deviceCount: number;
  offlineCount: number;
  addedAt: string;
  status: string;
  isNew: boolean;
}

export default function MyClientsPage() {
  const { token } = useAuth();
  const { t, tp, locale } = useI18n();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => { fetchClients(); }, []);

  async function fetchClients() {
    try {
      const res = await fetch(`${API}/api/my-clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setClients(data.clients);
    } catch (e) {
      console.error('Erreur chargement clients:', e);
    } finally {
      setLoading(false);
    }
  }

  function handleOpen(orgId: number) {
    navigate(`/clients/${orgId}/dashboard`);
  }

  const recentClients = [...clients]
    .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
    .slice(0, 5);

  const filtered = clients.filter(c =>
    c.orgName.toLowerCase().includes(search.toLowerCase()) ||
    c.orgSlug.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="flex gap-6 min-h-[calc(100vh-160px)]">
      {/* Sidebar */}
      <aside className="w-56 shrink-0 hidden lg:block">
        <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.15em] mb-3">{t('team_clients_recent')}</h3>
        <div className="space-y-1">
          {recentClients.map(c => (
            <button
              key={c.linkId}
              onClick={() => handleOpen(c.orgId)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left hover:bg-white hover:shadow-sm transition-all group"
            >
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0 shadow-sm">
                {c.orgName.slice(0, 2).toUpperCase()}
              </div>
              <span className="text-sm text-slate-700 group-hover:text-teal-700 truncate">{c.orgName}</span>
            </button>
          ))}
          {clients.length === 0 && (
            <p className="text-xs text-slate-400 px-3">{t('team_clients_none')}</p>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Hero premium — image d'installation + overlay teal */}
        <div className="relative overflow-hidden rounded-3xl text-white shadow-2xl h-60 sm:h-64">
          {/* Image de fond */}
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=500&fit=crop&crop=bottom"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Overlay sombre + teinte teal */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/80 to-teal-900/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
          {/* Pattern grille subtile */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
          {/* Halos colorés */}
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500/30 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />

          <div className="relative h-full flex items-center p-8">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-lg opacity-60" />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg ring-1 ring-white/20 text-white">
                  <IconBuilding className="w-8 h-8" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-cyan-400/30 text-cyan-300 text-[11px] font-semibold mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  {t('team_clients_badge')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  {t('team_clients_titleA')}{' '}
                  <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                    {t('team_clients_titleB')}
                  </span>
                </h1>
                <p className="text-slate-200 text-sm mt-1.5 drop-shadow">
                  {tp('team_clients_count', clients.length)} · {t('team_clients_subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-slate-800">{t('team_clients_my')}</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={t('team_clients_search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 w-64 bg-white"
              />
            </div>
            <button
              onClick={fetchClients}
              className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 hover:text-slate-800 hover:bg-slate-50 text-xs font-medium inline-flex items-center gap-1.5"
              title={t('team_refresh')}
            >
              <IconRefresh className="w-3.5 h-3.5" /> {t('team_refresh')}
            </button>
          </div>
        </div>

        {/* Empty state */}
        {clients.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center text-teal-600 mb-4">
              <IconBuilding className="w-8 h-8" />
            </div>
            <h3 className="text-base font-semibold text-slate-800">{t('team_clients_emptyTitle')}</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              {t('team_clients_emptyText')}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
              <IconSearch className="w-6 h-6" />
            </div>
            <p className="text-slate-500 text-sm">{t('team_clients_noMatch', { q: search })}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(client => (
              <button
                key={client.linkId}
                onClick={() => handleOpen(client.orgId)}
                className="group relative overflow-hidden bg-white rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-0.5 transition-all text-left"
              >
                {/* Liseré coloré en haut */}
                <div className="h-1 bg-gradient-to-r from-teal-400 via-cyan-400 to-sky-400" />
                {/* Glow décoratif au hover */}
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 opacity-0 group-hover:opacity-10 blur-2xl transition-opacity" />

                <div className="relative p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">
                      {client.orgName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-slate-900 truncate group-hover:text-teal-700 transition-colors">
                        {client.orgName}
                      </h3>
                      <p className="text-[11px] text-slate-400 font-mono truncate">{client.orgSlug}</p>
                    </div>
                    {client.isNew && (
                      <span className="px-2 py-0.5 text-[9px] font-bold bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full uppercase tracking-wider shadow-sm">
                        {t('team_clients_new')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-4">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {t('team_clients_active')}
                    </span>
                    <span className="text-slate-400">
                      {t('team_clients_since', { date: new Date(client.addedAt).toLocaleDateString(locale, { month: 'short', year: 'numeric' }) })}
                    </span>
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-400">{t('team_clients_openDashboard')}</span>
                    <span className="text-xs font-semibold text-teal-600 inline-flex items-center gap-1 translate-x-0 group-hover:translate-x-1 transition-transform">
                      {t('team_clients_access')}
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
